import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { FormRenderer } from '@stack/components';
import { useRegisterMutation, useRequestOtpMutation } from '@stack/data-access';
import Cookies from 'js-cookie';
import NextLink from 'next/link';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import { getSignUpFields } from './signUpForm';

const SignUpContainer = ({
  isSuccess,
}: {
  isSuccess: (value: boolean) => void;
}) => {
  // ======================= VARIABLES
  const schema = yup.object().shape({
    first_name: yup.string().required('First Name is required'),
    last_name: yup.string().required('Last Name is required'),
    user_mobile: yup.string().required('Phone Number is required').min(6),
    otp_no: yup.string().required('OTP NO is required'),
    email: yup.string().required('Email is required'),
    password: yup.string().required('Password is required'),
    confirm_password: yup.string().required('Confirm Password is required'),
  });

  // ======================= STATE
  const [password, setPassword] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [agree, setAgree] = useState(false);

  // ======================= API
  const [registerMutation, { loading }] = useRegisterMutation({
    onCompleted: (data) => {
      if (data?.Register?.status) {
        isSuccess(true);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  const [requestOTPMutation] = useRequestOtpMutation({
    onCompleted: (data) => {
      if (!data) return;
      const otp_no = data?.RequestOTP?.status;
      if (otp_no) {
        toast.success(
          `An OTP is sent to ${phone}. Please enter the code within 2 minutes`
        );
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  const handleOTPRequest = (val: string) => {
    setPhone('60' + val);
    requestOTPMutation({
      variables: {
        input: {
          user_mobile: '60' + val,
          otp_type: 'Register',
        },
      },
    });
  };

  // ======================= EVENTS
  const generateDeviceId = () => {
    const deviceId = uuidv4();
    Cookies.set('deviceId', deviceId, { expires: 365 });
    return deviceId;
  };

  const getDeviceId = () => {
    const deviceId = Cookies.get('deviceId');
    if (deviceId) {
      return deviceId;
    } else {
      return generateDeviceId();
    }
  };

  const handleSignUp = (value: any) => {
    registerMutation({
      variables: {
        input: {
          user_email: value.email,
          user_mobile: '60' + value.user_mobile,
          username: value.first_name + value.last_name,
          password: value.password,
          password_confirmation: value.confirm_password,
          otp_no: value.otp_no,
          device_id: getDeviceId(),
          first_name: value.first_name,
          last_name: value.last_name,
        },
      },
    });
  };

  // ======================= RENDER FUNCTIONS
  const renderAgreementCheckbox = () => {
    return (
      <Box sx={{ mb: 2, display: 'flex' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
          }
          label={
            <Typography
              variant={'body2'}
              sx={{ display: 'flex', flexWrap: 'wrap' }}
            >
              {`I agree to WeSettle's`}
              <Link
                marginLeft={1}
                marginRight={1}
                href={'https://www.wesettle.my/term-and-conditions'}
                component={NextLink}
              >
                Terms and Conditions
              </Link>
              and
              <Link
                marginLeft={1}
                marginRight={1}
                href={'https://www.wesettle.my/privacy-policy'}
                component={NextLink}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
      </Box>
    );
  };

  const renderFooter = () => {
    return (
      <Box
        sx={{
          mt: 12.5,
        }}
      >
        {renderAgreementCheckbox()}
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={!agree || loading}
        >
          Sign Up
        </Button>
      </Box>
    );
  };

  // ======================= VIEWS
  return (
    <FormRenderer
      fieldContainerProps={{
        position: 'relative',
        rowGap: 6,
        columnGap: {
          xs: 6,
          md: '1rem',
        },
      }}
      footerProps={{
        isLoading: loading,
      }}
      formOptions={{
        resolver: yupResolver(schema),
      }}
      schema={getSignUpFields({
        password: password,
        setPassword: setPassword,
        handleOTPRequest,
      })}
      footer={renderFooter()}
      onSubmit={handleSignUp}
    />
  );
};

export default SignUpContainer;
